import { BILLING_STATUS_DISPLAY, TIER_DISPLAY_NAMES, formatStorageBytes } from '@/atoms/subscription.atom';
import { CheckoutDialog } from '@/components/subscription/CheckoutDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useOrganization } from '@/hooks/useOrganizations';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const SubscriptionSettings = () => {
  const { subscription, features, loading, initialLoadComplete, error } = useSubscription();
  const organization = useOrganization();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const { t } = useTranslation('subscription');

  if (!initialLoadComplete || loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3 animate-pulse'>
            <div className='h-4 w-24 bg-slate-200 dark:bg-stone-800 rounded' />
            <div className='h-6 w-32 bg-slate-200 dark:bg-stone-800 rounded' />
            <div className='h-2 w-full bg-slate-200 dark:bg-stone-800 rounded mt-4' />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('settings.title')}</CardTitle>
          <CardDescription className='text-red-500'>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const storageUsedBytes = organization?.storageUsedBytes || 0;
  const ephemeralBytes = (organization as any)?.ephemeralBytes || 0;
  const storageLimitBytes = subscription.storageLimitBytes;
  const storagePercentage = storageLimitBytes > 0 ? Math.min((storageUsedBytes / storageLimitBytes) * 100, 100) : 0;
  const billingStatusInfo = BILLING_STATUS_DISPLAY[subscription.billingStatus];

  const getBadgeVariant = (color: string) => {
    switch (color) {
      case 'green':
        return 'success';
      case 'red':
        return 'destructive';
      case 'yellow':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle>{t('settings.title')}</CardTitle>
            <CardDescription>{t('settings.description')}</CardDescription>
          </div>
          <Badge variant={getBadgeVariant(billingStatusInfo.color)}>{billingStatusInfo.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className='space-y-5'>
        {/* Plan & billing summary */}
        <div className='flex gap-6'>
          <div className='flex-1'>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>{t('settings.plan')}</p>
            <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
              {TIER_DISPLAY_NAMES[subscription.tier]}
            </p>
          </div>
          {subscription.billingInterval && (
            <div className='flex-1'>
              <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>{t('settings.billing')}</p>
              <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
                {subscription.billingInterval === 'MONTHLY' ? t('settings.monthly') : t('settings.annual')}
              </p>
            </div>
          )}
          {subscription.collectionMethod && (
            <div className='flex-1'>
              <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>{t('settings.method')}</p>
              <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
                {subscription.collectionMethod === 'AUTOMATIC' ? t('settings.card') : t('settings.invoice')}
              </p>
            </div>
          )}
        </div>

        {/* Next billing date */}
        {subscription.currentPeriodEnd && features.isActive && (
          <div>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>
              {subscription.cancelAtPeriodEnd ? t('settings.cancelsOn') : t('settings.nextBilling')}
            </p>
            <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
              {dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY')}
            </p>
          </div>
        )}

        {/* Storage usage */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>{t('settings.storage')}</p>
            <p className='text-sm text-slate-700 dark:text-stone-300'>
              {formatStorageBytes(storageUsedBytes)} / {formatStorageBytes(storageLimitBytes)}
            </p>
          </div>
          <Progress value={storagePercentage} className='h-1.5' />
          {ephemeralBytes > 0 && (
            <p className='text-xs text-slate-500 dark:text-stone-400'>
              {t('settings.includingEphemeral', { size: formatStorageBytes(ephemeralBytes) })}
            </p>
          )}
        </div>

        {/* Scheduled changes */}
        {subscription.scheduledTier && subscription.scheduledChangeAt && (
          <div className='text-sm rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2'>
            <span className='text-blue-800 dark:text-blue-200'>
              {t('settings.planChangingTo', { tier: TIER_DISPLAY_NAMES[subscription.scheduledTier], date: dayjs(subscription.scheduledChangeAt).format('MMM D, YYYY') })}
            </span>
          </div>
        )}

        {/* CTA for free users */}
        {features.hasNoSubscription && (
          <Button className='w-full' onClick={() => setCheckoutOpen(true)}>
            {t('settings.upgradeToPaid')}
          </Button>
        )}
      </CardContent>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </Card>
  );
};
